import { useState, useRef, useCallback } from "react";
import { businessGallery, type BusinessGalleryImage } from "../../../mocks/businessData";
import { supabase } from "../../../lib/supabase";
import { useUser } from "../../../contexts/UserContext";

export default function GallerySection() {
  const { user } = useUser();
  const [images, setImages] = useState<BusinessGalleryImage[]>(businessGallery);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string[]>([]);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const setCover = (id: string) => {
    setImages((prev) => prev.map((img) => ({ ...img, isCover: img.id === id })));
  };

  const deleteImage = (id: string) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
  };

  const cover = images.find((i) => i.isCover);

  const uploadFiles = useCallback(async (files: FileList | File[]) => {
    const fileArr = Array.from(files);
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    const valid = fileArr.filter((f) => allowed.includes(f.type) && f.size <= 5 * 1024 * 1024);

    if (valid.length === 0) {
      setUploadError("Geçerli dosya bulunamadı. JPG, PNG veya WEBP, maks. 5MB.");
      return;
    }
    if (valid.length < fileArr.length) {
      setUploadError(`${fileArr.length - valid.length} dosya atlandı (geçersiz format veya boyut aşımı).`);
    }

    setUploading(true);
    setUploadProgress([]);
    const userId = user?.id ?? "business";
    const newImages: BusinessGalleryImage[] = [];

    for (const file of valid) {
      const ext = file.type.split("/")[1].replace("jpeg", "jpg");
      const timestamp = Date.now();
      const filePath = `business/${userId}/gallery/${timestamp}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;

      setUploadProgress((prev) => [...prev, `${file.name} yükleniyor...`]);

      const { error } = await supabase.storage
        .from("avatars")
        .upload(filePath, file, { upsert: false, contentType: file.type });

      if (error) {
        setUploadProgress((prev) => [...prev, `${file.name}: Hata — ${error.message}`]);
        continue;
      }

      const { data } = supabase.storage.from("avatars").getPublicUrl(filePath);
      const url = `${data.publicUrl}?v=${timestamp}`;

      newImages.push({
        id: `upload_${timestamp}_${Math.random().toString(36).slice(2)}`,
        url,
        alt: file.name.replace(/\.[^.]+$/, "").replace(/[_-]/g, " "),
        isCover: images.length === 0 && newImages.length === 0,
        uploadedAt: new Date().toLocaleDateString("tr-TR"),
      });

      setUploadProgress((prev) =>
        prev.map((p) => p.startsWith(file.name) ? `${file.name} ✓` : p),
      );
    }

    if (newImages.length > 0) {
      setImages((prev) => [...prev, ...newImages]);
    }

    setUploading(false);
    setTimeout(() => setUploadProgress([]), 3000);
  }, [user, images.length]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadError("");
      uploadFiles(e.target.files);
    }
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    setUploadError("");
    if (e.dataTransfer.files.length > 0) {
      uploadFiles(e.dataTransfer.files);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Toplam Fotoğraf", value: images.length, icon: "ri-image-line", color: "bg-sky-50 text-sky-700" },
          { label: "Kapak Fotoğrafı", value: cover ? "Seçili" : "Yok", icon: "ri-image-2-line", color: "bg-emerald-50 text-emerald-700" },
          { label: "Son Güncelleme", value: "18.03.2026", icon: "ri-calendar-line", color: "bg-stone-50 text-stone-600" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl border border-stone-100 p-4">
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl mb-2 ${s.color}`}>
              <i className={`${s.icon} text-base`} />
            </div>
            <div className="font-bold text-stone-800">{s.value}</div>
            <div className="text-xs text-stone-400">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Upload error */}
      {uploadError && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-500" />
          <p className="text-sm text-red-600">{uploadError}</p>
          <button onClick={() => setUploadError("")} className="ml-auto text-red-400 hover:text-red-600 cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>
      )}

      {/* Upload progress */}
      {uploadProgress.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 space-y-1">
          {uploadProgress.map((p, i) => (
            <p key={i} className="text-xs text-emerald-700 flex items-center gap-2">
              {p.includes("✓") ? (
                <i className="ri-check-line text-emerald-600" />
              ) : p.includes("Hata") ? (
                <i className="ri-close-line text-red-500" />
              ) : (
                <div className="w-3 h-3 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              )}
              {p}
            </p>
          ))}
        </div>
      )}

      {/* Upload Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
        className={`bg-white rounded-2xl border-2 border-dashed p-10 text-center transition-all cursor-pointer ${
          dragOver ? "border-emerald-400 bg-emerald-50" : uploading ? "border-stone-200 bg-stone-50 cursor-not-allowed" : "border-stone-200 hover:border-emerald-300 hover:bg-stone-50"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={handleFileChange}
          disabled={uploading}
        />
        <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-stone-100 mx-auto mb-4">
          {uploading ? (
            <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <i className="ri-upload-cloud-2-line text-2xl text-stone-400" />
          )}
        </div>
        <p className="font-semibold text-stone-700 mb-1">
          {uploading ? "Yükleniyor..." : "Fotoğraf Yükle"}
        </p>
        <p className="text-sm text-stone-400 mb-4">
          {uploading ? "Lütfen bekleyin" : "Dosyaları sürükleyin veya tıklayarak seçin"}
        </p>
        {!uploading && (
          <div className="flex items-center justify-center gap-3">
            <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-700 text-white rounded-xl text-sm font-semibold">
              <i className="ri-folder-open-line" />
              Dosya Seç
            </span>
            <span className="text-xs text-stone-400">JPG, PNG, WEBP · Maks. 5MB · Çoklu seçim</span>
          </div>
        )}
      </div>

      {/* Gallery Grid */}
      <div className="bg-white rounded-2xl border border-stone-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-stone-800">Mevcut Fotoğraflar</h3>
          <p className="text-xs text-stone-400">Kapak fotoğrafı seçmek için yıldız ikonuna tıklayın</p>
        </div>

        {images.length === 0 ? (
          <div className="py-12 text-center text-stone-400 text-sm">
            <i className="ri-image-line text-3xl block mb-2 text-stone-200" />
            Henüz fotoğraf yüklenmemiş
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((img) => (
              <div key={img.id} className="group relative rounded-xl overflow-hidden bg-stone-100" style={{ paddingBottom: "66.66%" }}>
                <img
                  src={img.url}
                  alt={img.alt}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />

                {/* Cover badge */}
                {img.isCover && (
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-emerald-600 text-white text-xs font-semibold rounded-lg">
                    Kapak
                  </div>
                )}

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => setCover(img.id)}
                    title="Kapak Yap"
                    className={`w-9 h-9 flex items-center justify-center rounded-xl text-sm transition-colors cursor-pointer ${
                      img.isCover
                        ? "bg-emerald-500 text-white"
                        : "bg-white/20 text-white hover:bg-emerald-500"
                    }`}
                  >
                    <i className="ri-star-line" />
                  </button>
                  <button
                    onClick={() => deleteImage(img.id)}
                    title="Sil"
                    className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/20 text-white hover:bg-red-500 text-sm transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>

                {/* Alt text */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5">
                  <p className="text-white text-xs truncate">{img.alt}</p>
                  <p className="text-white/50 text-xs">{img.uploadedAt}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tips */}
      <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <i className="ri-lightbulb-line text-amber-600 text-lg mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>İpucu:</strong> Yüksek kaliteli ve yatay formatlı fotoğraflar profilinizin öne çıkmasını sağlar.
            Kapak fotoğrafı, kategori listesinde ve arama sonuçlarında görünür. Birden fazla dosyayı aynı anda seçebilirsiniz.
          </div>
        </div>
      </div>
    </div>
  );
}
